import { ClearOutlined, SearchOutlined } from "@mui/icons-material";
import { IconButton, InputAdornment, OutlinedInput, type OutlinedInputProps, Tooltip } from "@mui/material";
import { useDebounce } from "Common/Hooks";
import { useEffect, useState } from "react";

interface ISearchBox extends OutlinedInputProps {
  handleClear?: () => void;
  disableDebouncedSearch?: boolean;
  handleSearch: (search: string) => void;
  hasBorder?: boolean;
  tooltip?: string;
}

export const SearchBox = (props: ISearchBox) => {
  const { placeholder, value, tooltip, handleClear, handleSearch, hasBorder, disableDebouncedSearch, ...rest } = props;

  const [searchTerm, setSearchTerm] = useState<string>(typeof value === "string" ? value : "");

  useEffect(() => {
    if (typeof value === "string") {
      setSearchTerm(value);
    }
  }, [value]);

  // Create debounced version of handleSearch with 500ms delay
  const debouncedHandleSearch = useDebounce((search: string) => {
    handleSearch(search);
  }, 500);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    setSearchTerm(search);

    // Use either debounced or immediate search based on disableDebouncedSearch prop
    if (disableDebouncedSearch) {
      handleSearch(search);
    } else {
      debouncedHandleSearch(search);
    }
  };

  return (
    <Tooltip title={tooltip ?? placeholder ?? "Search"} placement="bottom">
      <OutlinedInput
        id={`search-box-${tooltip ?? "default"}`}
        fullWidth
        placeholder={placeholder ?? "Search"}
        value={searchTerm}
        onChange={handleChange}
        startAdornment={
          <InputAdornment position="start">
            <SearchOutlined fontSize="small" />
          </InputAdornment>
        }
        endAdornment={
          <>
            {handleClear && searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  id="clear-search-btn"
                  edge="end"
                  onClick={() => {
                    setSearchTerm("");
                    handleClear();
                  }}
                >
                  <ClearOutlined />
                </IconButton>
              </InputAdornment>
            ) : null}
          </>
        }
        sx={(theme) => ({
          "& .MuiOutlinedInput-notchedOutline": {
            ...(!hasBorder && { border: "none" }),
          },
          "& .MuiOutlinedInput-input": {
            paddingY: theme.spacing(1),
          },
        })}
        {...rest}
      />
    </Tooltip>
  );
};
